import { useMutation, useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useAppSelector } from 'src/services/redux/app/store';
import { SalesTarget, SalesTargetCreate, SalesTargetUpdate, DashboardData, TargetsQuery } from 'src/types/salesTargets';

const REACT_APP_API_URL = `${process.env.REACT_APP_API_URL}`;

/**
 * Hedefleri listele
 */
export const useSalesTargets = (params: Partial<TargetsQuery>, onError?: any) => {
  const requestUrl = REACT_APP_API_URL + '/quarterly-sales-targets';

  const fetchData = async () => {
    const res = await axios.get<SalesTarget[]>(`${requestUrl}`, { params });
    return res?.data || [];
  };

  return useQuery(['sales-targets', params], fetchData, {
    refetchOnWindowFocus: false,
    onError,
    staleTime: 60000,
    retry: false,
  });
};

/**
 * Ürün grupları listesi
 */
export const useProductGroups = (onError?: any) => {
  const { user } = useAppSelector((state) => state.auth);
  const requestParams = { company_id: user?.company_id };
  const requestUrl = REACT_APP_API_URL + '/quarterly-sales-targets/product-groups/list';

  const fetchData = async () => {
    const res = await axios.get<string[]>(`${requestUrl}`, { params: requestParams });
    return res?.data || [];
  };

  return useQuery(['product-groups', requestParams], fetchData, {
    refetchOnWindowFocus: false,
    onError,
    staleTime: 300000,
    retry: false,
  });
};

/**
 * Dashboard performans verileri
 */
export const useDashboardPerformance = (onError?: any) => {
  const { user } = useAppSelector((state) => state.auth);
  const requestParams = { company_id: user?.company_id };
  const requestUrl = REACT_APP_API_URL + '/quarterly-sales-targets/dashboard/performance';

  const fetchData = async () => {
    const res = await axios.get<DashboardData>(`${requestUrl}`, { params: requestParams });
    return res?.data;
  };

  return useQuery(['dashboard-performance', requestParams], fetchData, {
    refetchOnWindowFocus: false,
    onError,
    staleTime: 60000,
    retry: false,
  });
};

/**
 * Yeni hedef oluştur
 */
export const useCreateTarget = (onSuccess?: any, onError?: any) => {
  const requestUrl = REACT_APP_API_URL + '/quarterly-sales-targets';

  return useMutation(
    async (data: SalesTargetCreate) => {
      const res = await axios.post<SalesTarget>(`${requestUrl}`, data);
      return res?.data;
    },
    {
      onSuccess,
      onError,
    },
  );
};

/**
 * Hedef güncelle
 */
export const useUpdateTarget = (onSuccess?: any, onError?: any) => {
  return useMutation(
    async ({ id, data }: { id: number; data: SalesTargetUpdate }) => {
      const requestUrl = REACT_APP_API_URL + `/quarterly-sales-targets/${id}`;
      const res = await axios.patch<SalesTarget>(`${requestUrl}`, data);
      return res?.data;
    },
    {
      onSuccess,
      onError,
    },
  );
};

/**
 * Hedef sil
 */
export const useDeleteTarget = (onSuccess?: any, onError?: any) => {
  return useMutation(
    async (id: number) => {
      const requestUrl = REACT_APP_API_URL + `/quarterly-sales-targets/${id}`;
      const res = await axios.delete(`${requestUrl}`);
      return res?.data;
    },
    {
      onSuccess,
      onError,
    },
  );
};

/**
 * Belirli bir ürün grubundaki DIA markalarını getir
 */
export const useDIABrands = (productGroup: string | null, onError?: any) => {
  const requestUrl = REACT_APP_API_URL + '/quarterly-sales-targets/brands';

  const fetchData = async () => {
    if (!productGroup) return [];
    const res = await axios.get<string[]>(`${requestUrl}`, { params: { product_group: productGroup } });
    return res?.data || [];
  };

  return useQuery(['dia-brands', productGroup], fetchData, {
    refetchOnWindowFocus: false,
    onError,
    enabled: !!productGroup,
    staleTime: 600000, // 10 dakika cache
    retry: false,
  });
};

/**
 * Manuel sync tetikle
 */
export const useSyncTargets = (onSuccess?: any, onError?: any) => {
  const requestUrl = REACT_APP_API_URL + '/quarterly-sales-targets/sync';

  return useMutation(
    async () => {
      const res = await axios.post(`${requestUrl}`);
      return res?.data;
    },
    {
      onSuccess,
      onError,
    },
  );
};
